/**
 * /auth/return — completes a centralized-login round trip and resumes the
 * stashed deep link. Three arrival paths, checked in THIS order:
 *
 *  1. `?code=` — saas-auth's authorization-code delivery
 *     (`app.auth.oauth-code-delivery.enabled`, the intended posture): a
 *     single-use, short-TTL code exchanged backchannel via
 *     POST {authOrigin}/api/auth/session/exchange — credentialed (the HttpOnly
 *     binding cookie), pre-session (no bearer, no CSRF; the route establishes
 *     the session) — for an AuthTokenResponse.
 *  2. A URL-delivered access token in the query or fragment (`token` /
 *     `accessToken`, optional `expiresIn`) — the legacy delivery when the flag
 *     is off.
 *  3. Nothing in the URL — the landing return (SASATH-0330): on a product-domain
 *     console the SSO success hops through the paired auth host's
 *     /api/auth/oauth/land, which sets the product refresh + CSRF cookies THERE
 *     and 302s here with no token and no code. The session is adopted by a
 *     credentialed boot refresh against the bootstrapped authOrigin.
 *
 * Either way the token-bearing URL is stripped BEFORE anything else, the token
 * lives in memory only, and a handoff without a product-admin grant ends at
 * /auth/no-access — never a re-login loop. A rejected code (400 CODE_INVALID:
 * unknown, expired, replayed) or a landing return whose refresh yields no
 * session ends at /auth/signed-out, whose "Sign in again" button is the only
 * way back — never an automatic redirect, which with a bad code or a missing
 * cookie would loop.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PreAppLayout } from '@/components/PreAppLayout';
import { authApi } from '@/api/auth';
import { bootRefresh, popDeepLink } from '@/auth/silentRefresh';
import { getAccessToken, getClaims, getExpiresAt, setCsrfToken } from '@/auth/authStore';
import { useAuth } from '@/auth/AuthContext';
import { useCopy } from '@/hooks/useCopy';
import { usePageTitle } from '@/components/page/usePageTitle';

/** Access-token lifetime in seconds → ISO-8601 instant; null when absent or
 *  malformed. Shared by both delivery paths. */
export function expiresAtFromSeconds(value: unknown): string | null {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const d = new Date(Date.now() + seconds * 1000);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

export function AuthReturn() {
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const ran = useRef(false);
  const { t } = useCopy();
  const heading = t('auth.returning', 'Completing sign-in…');
  usePageTitle(heading);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (getAccessToken()) {
      const deep = popDeepLink() ?? '/';
      navigate(deep, { replace: true });
      return;
    }

    const qs = new URLSearchParams(window.location.search);
    const hs = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const pick = (key: string) => qs.get(key) ?? hs.get(key);

    /** Adopt a minted access token: store it, gate on the grant, resume. */
    const adopt = (accessToken: string, expiresAt: string | null): void => {
      setAuthed(accessToken, expiresAt);
      if (!getClaims()?.productAdminOf) {
        navigate('/auth/no-access', { replace: true });
        return;
      }
      const deep = popDeepLink() ?? '/';
      navigate(deep, { replace: true });
    };

    // Path 1 — authorization code. Scrub the URL first: the code is single-use
    // and must never survive in history, a bookmark, or a referrer.
    const code = pick('code');
    if (code) {
      window.history.replaceState(null, '', window.location.pathname);
      void authApi
        .exchange(code)
        .then((body) => {
          if (!body.accessToken) {
            navigate('/auth/signed-out', { replace: true });
            return;
          }
          setCsrfToken(body.csrfToken ?? null);
          adopt(body.accessToken, expiresAtFromSeconds(body.expiresIn));
        })
        .catch(() => {
          navigate('/auth/signed-out', { replace: true });
        });
      return;
    }

    // Path 2 — URL-delivered access token (legacy delivery, flag off).
    const accessToken = pick('accessToken') ?? pick('token');
    const expiresIn = pick('expiresIn');
    const errorParam = pick('error');

    if (accessToken) {
      window.history.replaceState(null, '', window.location.pathname);
      adopt(accessToken, expiresAtFromSeconds(expiresIn));
      return;
    }

    // An explicit error from the login side is a denial, not a landing.
    if (errorParam) {
      navigate('/auth/no-access', { replace: true });
      return;
    }

    // Path 3 — landing return: nothing in the URL. The paired auth host set the
    // product refresh cookie during the landing hop; a credentialed boot refresh
    // against the bootstrapped authOrigin is how the session is adopted.
    void bootRefresh().then((refreshed) => {
      if (!refreshed) {
        navigate('/auth/signed-out', { replace: true });
        return;
      }
      const token = getAccessToken();
      if (token) setAuthed(token, getExpiresAt());
      if (!getClaims()?.productAdminOf) {
        navigate('/auth/no-access', { replace: true });
        return;
      }
      const deep = popDeepLink() ?? '/';
      navigate(deep, { replace: true });
    });
  }, [navigate, setAuthed]);

  return (
    <PreAppLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <LoadingSpinner />
        <Typography variant="body2" component="h1" sx={{ color: 'text.secondary' }}>
          {heading}
        </Typography>
      </Box>
    </PreAppLayout>
  );
}
